import { createStandaloneToast, theme } from '@chakra-ui/react';
import {
  MutationCache,
  type MutationOptions,
  QueryClient,
} from '@tanstack/react-query';
import { type AxiosError, isAxiosError } from 'axios';

import i18n from '@/lib/i18n/client';
import { AVAILABLE_LANGUAGES } from '@/lib/i18n/constants';

const { toast } = createStandaloneToast({
  defaultOptions: { position: 'top-right' },
  theme: {
    ...theme,
    direction:
      AVAILABLE_LANGUAGES.find(({ key }) => key === i18n.language)?.dir ??
      'ltr',
  },
});

const shouldUseDefaultErrorHandler = (options: MutationOptions) => {
  return !options.onError || options.forceUseDefaultErrorHandler;
};

const mutationCache = new MutationCache({
  // eslint-disable-next-line max-params
  onError: (error, _variables, _context, mutation) => {
    const { disableDefaultErrorHandler } = mutation.options;

    if (
      shouldUseDefaultErrorHandler(mutation.options) &&
      !disableDefaultErrorHandler
    ) {
      const { description, title } = extractErrorMessage(error);
      toast({
        title,
        description,
        status: 'error',
      });
    }
  },
});

export const queryClient = new QueryClient({
  mutationCache,
  defaultOptions: {
    queries: {
      retry(failureCount, error) {
        console.log(error);
        if (isAxiosError(error)) {
          if (error.response?.status === 401) return false;
          if (error.response?.status === 403) return false;
          if (error.response?.status === 404) return false;
        }

        // retry 3 times
        return failureCount <= 3;
      },
    },
  },
});

export function extractErrorMessage(
  error: AxiosError<ExplicitAny, ExplicitAny> | ExplicitAny
): { title?: string; description?: string } {
  let description = i18n.t('common:error.toast.message.default');
  let title = i18n.t('common:error.toast.title.default');

  if (!isAxiosError(error)) {
    return { description, title };
  }

  // Detect network errors
  const msg = error.message?.toLowerCase() || '';
  if (error.code === 'ECONNABORTED' || msg.includes('timeout')) {
    return {
      title: i18n.t('interview:error.toast.title.networkTimeout'),
      description: i18n.t('interview:error.toast.description.networkTimeout'),
    };
  }
  if (
    error.code === 'ECONNRESET' ||
    msg.includes('network error') ||
    msg.includes('connection reset')
  ) {
    return {
      title: i18n.t('interview:error.toast.title.networkError'),
      description: i18n.t('interview:error.toast.description.networkError'),
    };
  }

  if (error.response?.data?.title) {
    title = error.response.data.title;
  }

  const isSupportedError = (status: number): status is 401 | 403 | 404 =>
    [401, 403, 404].includes(status);

  if (error.response?.data?.detail) {
    description = error.response.data.detail;
  } else {
    const status = Number(error.response?.status);
    if (isSupportedError(status)) {
      description = i18n.t(`common:error.toast.message.${status}`);
      title = i18n.t(`common:error.toast.title.${status}`);
    }
  }

  return { description, title };
}
